import {SupabaseClient} from '@supabase/supabase-js'
import {InviteNew, InviteStatus, InviteRowInsert, zInvite} from 'components/portal/admin/inviteTypes'
import {pick} from 'remeda'
import {Database} from 'utils/db'
import {nanoid} from 'nanoid'
import {zProfileAdminRow} from 'components/portal/profileTypes'
import {parseList, safeParseList} from 'shared/type'
import {asyncMap} from 'modern-async'
import {err, ok, okOrErrPartition} from 'shared/promise'

export const processNewInvites = async ({
  sb,
  workspace_id,
  newInvites
}: {
  sb: SupabaseClient<Database>
  workspace_id: string
  newInvites: InviteNew[]
}) => {
  const invites = await sb
    .from('invite')
    .insert(
      newInvites.map(
        i =>
          ({
            workspace_id,
            ...pick(i, ['email', 'roles']),
            status: 'pending' as InviteStatus,
            cfg: {token: nanoid(15)}
          }) as InviteRowInsert
      )
    )
    .select('*')
    .then(r => (r.error ? err(`error creating invites: ${r.error.message}`) : parseList(zInvite, r.data ?? [])))
  if (!invites.ok) return invites

  // Update existing profiles with the new roles if for some reason a profile already exists. This can happen in the global workspace
  // because profiles are already created to track tips and other global things.
  const existingProfilesWithInvites = await sb
    .from('profile_user_admin')
    .select('id, cfg, email')
    .in(
      'email',
      newInvites.map(i => i.email)
    )
    .match({workspace_id})
    .then(r =>
      r.error ?
        err(`error fetching profiles as part of processing invites: ${r.error.message}`)
      : ok(
          safeParseList(zProfileAdminRow.pick({id: true, cfg: true, email: true}), r.data ?? [])
            .map(profile => ({
              profile,
              invite: newInvites.find(i => i.email == profile.email)!
            }))
            .filter(i => i.invite)
        )
    )
  if (!existingProfilesWithInvites.ok) return existingProfilesWithInvites

  const [_, updateProfileErrs] = await asyncMap(
    existingProfilesWithInvites,
    async ({profile: {id, cfg}, invite}) =>
      await sb
        .from('profile')
        .update({cfg: {...cfg, roles: [...cfg.roles, ...invite.roles]}})
        .match({id})
        .then(r => (r.error ? err(`error updating profile ${id}: ${r.error.message}`) : ok())),
    4
  ).then(rs => okOrErrPartition(rs))
  if (updateProfileErrs.length > 0)
    return err(`error updating profiles as part of processing invites: ${updateProfileErrs.join(', ')}`)

  return invites
}
